import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Button,
  Image,
  Spinner,
  Card,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faArrowLeft,
  faEnvelope,
  faMobileAlt,
  faKey,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import toast, { Toaster } from "react-hot-toast";
import { TOKEN_KEY } from "../../redux/actions/auth";
import { SET_TOKEN, LOGIN, CLEAR_MFA } from "../../redux/actions/actionTypes";
import { postApi } from "../../redux/api";
import { Routes } from "../../routes";
import { mainTreasuryModules } from "./user-admin/modules";
import logo from "../../assets/img/state-govt-logo.png";
import "./signin.css";

const getDefaultRoute = (func) => {
  let allRoutes = [];
  mainTreasuryModules.forEach(m => {
    if(m.subMenu) {
      m.subMenu.forEach(s => allRoutes.push(s))
    } else {
      allRoutes.push(m)
    }
  })
  let foundRoute = allRoutes.find((a) => a.title === func);
  return foundRoute;
};

const maskPhone = (phone) => {
  if (!phone || phone.length < 6) return phone || '';
  return phone.slice(0, 3) + '****' + phone.slice(-3);
};

const maskEmail = (email) => {
  if (!email || !email.includes('@')) return email || '';
  const [name, domain] = email.split('@');
  const maskedName = name.length > 2 ? name[0] + '***' + name[name.length - 1] : name;
  return `${maskedName}@${domain}`;
};

const MFAVerify = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const mfaToken = useSelector((state) => state.auth.mfaToken);
  const mfaUser = useSelector((state) => state.auth.mfaUser);

  const isTotpEnabled = !!mfaUser?.totp_enabled;

  const [step, setStep] = useState("select"); // "select" | "verify"
  const [method, setMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [codeDigits, setCodeDigits] = useState(["", "", "", "", "", ""]);
  const codeInputRefs = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!mfaToken) {
      navigate("/sign-in");
    }
  }, [mfaToken, navigate]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startOtpTimer = useCallback(() => {
    setOtpTimer(60);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const sendOtpForMethod = useCallback(async (targetMethod) => {
    if (!mfaToken) return;
    setSendingOtp(true);
    try {
      const resp = await postApi("mfa/send-otp", { method: targetMethod, username: mfaUser?.username });
      if (resp.success) {
        toast.success(`Verification code sent via ${targetMethod}`);
        startOtpTimer();
      } else {
        toast.error(resp.msg || "Failed to send code");
      }
    } catch {
      toast.error("Failed to send code");
    } finally {
      setSendingOtp(false);
    }
  }, [mfaToken, mfaUser, startOtpTimer]);

  const handleSelectMethod = (selectedMethod) => {
    setMethod(selectedMethod);
    setCodeDigits(["", "", "", "", "", ""]);
    setStep("verify");
    if (selectedMethod === "email" || selectedMethod === "sms") {
      sendOtpForMethod(selectedMethod);
    }
  };

  const handleResendOtp = useCallback(async () => {
    if (method) {
      await sendOtpForMethod(method);
    }
  }, [sendOtpForMethod, method]);

  const handleCodeDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...codeDigits];
    newDigits[index] = value;
    setCodeDigits(newDigits);

    if (value && index < 5 && codeInputRefs.current[index + 1]) {
      codeInputRefs.current[index + 1].focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !codeDigits[index] && index > 0 && codeInputRefs.current[index - 1]) {
      codeInputRefs.current[index - 1].focus();
    }
    if (e.key === "Enter" && index === 5) {
      handleVerify();
    }
  };

  const handleVerify = async () => {
    const fullCode = codeDigits.join("");
    if (fullCode.length !== 6 || !mfaToken || !method) return;

    setLoading(true);
    try {
      let resp;
      if (method === "authenticator") {
        resp = await postApi("mfa/verify/totp", {
          code: fullCode,
          username: mfaUser?.username,
        });
      } else {
        resp = await postApi("mfa/verify-otp", {
          code: fullCode,
          username: mfaUser?.username,
          method,
        });
      }

      if (resp.success) {
        const finalResp = await postApi("mfa/complete", {
          mfa_token: mfaToken,
          method,
        });

        if (finalResp.success) {
          localStorage.setItem(TOKEN_KEY, finalResp.token);
          dispatch({ type: SET_TOKEN, payload: finalResp.token });
          dispatch({ type: LOGIN, payload: finalResp.user });
          dispatch({ type: CLEAR_MFA });

          toast.success("Verification successful!");
          navigateToDashboard(finalResp.user);
        } else {
          toast.error(finalResp.msg || "Failed to complete authentication");
        }
      } else {
        toast.error(resp.msg || "Invalid verification code");
        setCodeDigits(["", "", "", "", "", ""]);
        if (codeInputRefs.current[0]) codeInputRefs.current[0].focus();
      }
    } catch {
      toast.error("Connection error");
    } finally {
      setLoading(false);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim().replace(/\D/g, "").slice(0, 6);
    if (pasteData.length === 6) {
      setCodeDigits(pasteData.split(""));
      if (codeInputRefs.current[5]) codeInputRefs.current[5].focus();
    }
  };

  const handleBackToLogin = () => {
    dispatch({ type: CLEAR_MFA });
    navigate("/sign-in");
  };

  const navigateToDashboard = (user) => {
    const firstItem = user.functionalities ? user.functionalities.split(",")[0] : null;
    if (firstItem) {
      const route = getDefaultRoute(firstItem);
      if (route) {
        navigate(route.route);
        return;
      }
    }
    navigate(Routes.approval.approvalIndex);
  };

  return (
    <main>
      <Toaster position="top-right" toastOptions={{ duration: 4000, style: { background: "#343a40", color: "#fff" } }} />
      <section className="d-flex align-items-center my-auto py-2" style={{ minHeight: "100vh" }}>
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <div className="bg-white shadow-soft border rounded border-light p-4 pb-lg-3 p-lg-5 w-100 fmxw-500">
                <div className="text-center text-md-center mb-4 mt-md-0">
                  <Image src={logo} className="kifmis-logo" />
                  <h5 className="mb-0 mt-2">
                    Multi-Factor Authentication
                  </h5>
                  <p className="text-muted small mt-1">
                    Verify your identity to continue
                  </p>
                </div>

                {step === "select" ? (
                  <>
                    <div className="text-center mb-4">
                      <h6 className="fw-bold mb-1">Select Verification Method</h6>
                      <p className="text-muted small mb-0">
                        Choose how you would like to receive your security code
                      </p>
                    </div>

                    <div className="d-grid gap-3 mb-4">
                      <Card
                        className="border shadow-sm p-3 cursor-pointer text-start bg-light-hover"
                        style={{ cursor: "pointer", transition: "all 0.2s" }}
                        onClick={() => handleSelectMethod("email")}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <div className="p-2 rounded bg-primary text-white me-3 d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px" }}>
                              <FontAwesomeIcon icon={faEnvelope} />
                            </div>
                            <div>
                              <h6 className="mb-0 fw-bold">Email Verification</h6>
                              <small className="text-muted">
                                {mfaUser?.email ? `Send code to ${maskEmail(mfaUser.email)}` : "Send code to your registered email"}
                              </small>
                            </div>
                          </div>
                          <FontAwesomeIcon icon={faChevronRight} className="text-muted small me-1" />
                        </div>
                      </Card>

                      <Card
                        className="border shadow-sm p-3 cursor-pointer text-start bg-light-hover"
                        style={{ cursor: "pointer", transition: "all 0.2s" }}
                        onClick={() => handleSelectMethod("sms")}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <div className="p-2 rounded bg-success text-white me-3 d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px" }}>
                              <FontAwesomeIcon icon={faMobileAlt} />
                            </div>
                            <div>
                              <h6 className="mb-0 fw-bold">SMS Verification</h6>
                              <small className="text-muted">
                                {mfaUser?.phone ? `Send code to ${maskPhone(mfaUser.phone)}` : "Send code to your registered phone"}
                              </small>
                            </div>
                          </div>
                          <FontAwesomeIcon icon={faChevronRight} className="text-muted small me-1" />
                        </div>
                      </Card>

                      {isTotpEnabled && (
                        <Card
                          className="border shadow-sm p-3 cursor-pointer text-start bg-light-hover"
                          style={{ cursor: "pointer", transition: "all 0.2s" }}
                          onClick={() => handleSelectMethod("authenticator")}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <div className="p-2 rounded bg-info text-white me-3 d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px" }}>
                                <FontAwesomeIcon icon={faKey} />
                              </div>
                              <div>
                                <h6 className="mb-0 fw-bold">Authenticator App (2FA)</h6>
                                <small className="text-muted">Use code from Google Authenticator or Authy</small>
                              </div>
                            </div>
                            <FontAwesomeIcon icon={faChevronRight} className="text-muted small me-1" />
                          </div>
                        </Card>
                      )}
                    </div>

                    <div className="text-center mt-3">
                      <Button
                        variant="link"
                        className="p-0 text-muted small text-decoration-none"
                        onClick={handleBackToLogin}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                        Back to sign in
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <Button
                        variant="link"
                        className="p-0 text-muted small text-decoration-none"
                        onClick={() => setStep("select")}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                        Change verification method
                      </Button>
                    </div>

                    <div className="text-center mb-3">
                      {method === "authenticator" ? (
                        <p className="text-muted small">
                          Open your authenticator app (Google Authenticator, Authy, etc.) and enter the 6-digit code
                        </p>
                      ) : (
                        <p className="text-muted small">
                          {sendingOtp
                            ? "Sending verification code..."
                            : "Enter the 6-digit code sent via " + (method === "sms" ? `SMS to ${maskPhone(mfaUser?.phone)}` : `Email to ${maskEmail(mfaUser?.email)}`)}
                          {otpTimer > 0 ? " (" + otpTimer + "s)" : ""}
                        </p>
                      )}
                    </div>

                    {sendingOtp && method !== "authenticator" ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" className="mb-2" />
                        <p className="text-muted small">Sending verification code...</p>
                      </div>
                    ) : (
                      <>
                        <div className="otp-input-group justify-content-center" onPaste={handlePaste}>
                          {[0, 1, 2, 3, 4, 5].map((i) => (
                            <input
                              key={i}
                              ref={(el) => (codeInputRefs.current[i] = el)}
                              type="text"
                              maxLength={1}
                              className="otp-input"
                              value={codeDigits[i] || ""}
                              onChange={(e) => handleCodeDigitChange(i, e.target.value)}
                              onKeyDown={(e) => handleCodeKeyDown(i, e)}
                              autoFocus={i === 0}
                              autoComplete="one-time-code"
                            />
                          ))}
                        </div>

                        <Button
                          variant="primary"
                          className="w-100 mt-3"
                          onClick={handleVerify}
                          disabled={codeDigits.join("").length !== 6 || loading}
                        >
                          {loading ? (
                            <><Spinner animation="border" size="sm" className="me-1" /> Verifying...</>
                          ) : (
                            <><FontAwesomeIcon icon={faShieldAlt} className="me-1" /> Verify</>
                          )}
                        </Button>

                        {method !== "authenticator" && (
                          <div className="text-center mt-3">
                            {otpTimer > 0 ? (
                              <small className="text-muted">Resend code in {otpTimer}s</small>
                            ) : (
                              <Button variant="link" className="p-0 text-decoration-none" onClick={handleResendOtp}>
                                Resend code
                              </Button>
                            )}
                          </div>
                        )}

                        <div className="text-center mt-3">
                          <Button
                            variant="link"
                            className="p-0 text-muted small text-decoration-none"
                            onClick={handleBackToLogin}
                          >
                            <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                            Back to sign in
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default MFAVerify;
